import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About JBS Consulting – Turnaround & Restructuring Experts',
  description:
    'Learn about JBS Consulting, LLC — the trusted turnaround partner for distressed small and mid-market businesses. Empathetic, affordable, and structured solutions on a fixed fee and fixed timeline.',
  url: `${siteUrl}/about`,
  publisher: {
    '@type': 'Organization',
    name: 'JBS Consulting, LLC',
    url: siteUrl,
    email: 'info@jbsconsulting.xyz',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '200 S. Wacker Drive, Suite 3100',
      addressLocality: 'Chicago',
      addressRegion: 'IL',
      postalCode: '60606',
      addressCountry: 'US',
    },
  },
};

export default function About() {
  const values = [
    {
      icon: 'ri-heart-line',
      title: 'Empathy First',
      description: 'We understand that financial distress is stressful and personal. We approach every engagement with compassion, discretion, and genuine care for your business and your people.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Affordable & Transparent',
      description: 'We work on a fixed fee and fixed timeline — no billing surprises. Our goal is to deliver maximum value at a cost that makes sense for struggling businesses.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Structured Approach',
      description: 'Every engagement follows a proven, disciplined methodology. We deliver tools and recommendations that maximize profitability, pay back creditors, and save jobs.'
    },
    {
      icon: 'ri-team-line',
      title: 'Long-Term Partnership',
      description: 'We are not just advisors — we are partners in your recovery. We stay engaged through implementation and beyond to ensure lasting results.'
    }
  ];

  const industries = [
    { icon: 'ri-building-2-line', name: 'Construction' },
    { icon: 'ri-group-line', name: 'Staffing' },
    { icon: 'ri-store-2-line', name: 'Retail' },
    { icon: 'ri-computer-line', name: 'Software' },
    { icon: 'ri-restaurant-line', name: 'Food & Beverage' },
    { icon: 'ri-truck-line', name: 'Transportation' },
    { icon: 'ri-hospital-line', name: 'Healthcare' },
    { icon: 'ri-tools-line', name: 'Manufacturing' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <title>About JBS Consulting – Turnaround &amp; Restructuring Experts</title>
      <meta name="description" content="JBS Consulting, LLC is the trusted turnaround partner for distressed small and mid-market businesses. Empathetic, affordable, and structured solutions on a fixed fee and fixed timeline." />
      <meta name="keywords" content="about JBS Consulting, business turnaround experts, restructuring consultants, distressed business advisors" />
      <link rel="canonical" href={`${siteUrl}/about`} />
      <meta property="og:title" content="About JBS Consulting – Turnaround & Restructuring Experts" />
      <meta property="og:description" content="The trusted turnaround partner for distressed businesses. Learn about our mission, values, and the industries we serve." />
      <meta property="og:url" content={`${siteUrl}/about`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(schemaJsonLd)}</script>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a52a9f9ba564c89a1e245a6f65ff44dd.jpg"
            alt="JBS Consulting Team"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-slate-900/80"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">About JBS Consulting</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            We are the trusted turnaround partner for distressed small and mid-market businesses — delivering empathetic, affordable, and structured solutions on a fixed fee and fixed timeline.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-wider text-orange-500 font-semibold mb-4 block">Our Mission</span>
              <h2 className="text-4xl font-serif font-bold text-slate-900 mb-6">The Backstop for Struggling Businesses</h2>
              <p className="text-lg text-slate-600 mb-6">
                Small and mid-market companies create most of the jobs and pay most of the taxes in the United States. Yet until JBS Consulting, when these businesses ran into financial and operational trouble, there was no consistent place or institution for them to turn to for help.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                We are passionate about collaborating with clients to solve problems at an affordable cost and in an empathetic manner. Using a structured approach, we deliver tools and recommendations that maximize profitability, pay back creditors, and save jobs.
              </p>
              <p className="text-lg text-slate-600">
                Our business is your business. We are dedicated to helping you grow and improve your business with the same level of care as an owner. We pride ourselves on our ability to expertly analyze a situation and collaborate with our clients to deliver thoughtful solutions for a fixed fee and on a fixed timeline.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Business%20turnaround%20consultant%20presenting%20a%20recovery%20roadmap%20on%20a%20whiteboard%20to%20a%20small%20group%20of%20attentive%20business%20owners%20in%20a%20bright%20modern%20conference%20room%2C%20collaborative%20problem-solving%20atmosphere%2C%20professional%20business%20photography%20with%20warm%20natural%20lighting%2C%20clean%20contemporary%20office%20environment%20showing%20expertise%20and%20hope&width=600&height=700&seq=about-mission-jbs&orientation=portrait"
                alt="JBS Consulting mission"
                className="rounded-lg shadow-2xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-slate-900 mb-4">Industries We Serve</h2>
            <p className="text-lg text-slate-600">We have deep experience across a wide range of industries</p>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center">
                  <i className={`${industry.icon} text-2xl text-orange-500`}></i>
                </div>
                <span className="text-xs font-medium text-slate-700 text-center">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-slate-900 mb-4">Our Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">The principles that guide every engagement and every recommendation we make</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-slate-900 mb-4">{value.title}</h3>
                <p className="text-sm text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif font-bold text-slate-900 mb-6">Ready to Turn Your Business Around?</h2>
          <p className="text-lg text-slate-600 mb-8">
            Contact JBS Consulting today for a confidential, no-obligation assessment of your business situation.
          </p>
          <a href="/contact">
            <button className="px-8 py-4 bg-orange-500 text-white text-base font-medium rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer">
              Get a Free Assessment
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
